import { FeedbackInvitationState, IDiscussion, IFeedbackInvitation, IUser } from "@api/schema"
import { AvailableContentItems } from "@components/common/contentmanagement/ContentManagementHelper"
import { IFilterCriteria } from "@redux/helper/actions"
import { AppState } from "@redux/reducer"
import { EntityType, OrderTypes } from "@redux/reduxTypes"
import { idFromIModelOrIRI } from "@services/util"

import { selectCollection } from "./data"


/**
 * This interface defines, what filter criteria are accepted by the API for FeedbackInvitations
 */
export interface IFeedbackInvitationFilterCriteria extends IFilterCriteria {
  /** IRI of one or many IProcess, IProject, IUser */
  relatedObject?: string | string[]
  /** IRI of one or many IProcess, IProject, IUser or "all" */
  receiver?: string | string[]
  /** ID of one or many IUser, who may have created an FBI */
  createdBy?: string | string[]
  /** current state to be filtered for */
  state?: FeedbackInvitationState
  /** if true only FBIs with anonymousFeedback allowed are provided */
  anonymousFeedback?: boolean
  /** order for creation date */
  "order[createdAt]"?: OrderTypes
  /** order for title */
  "order[title]"?: OrderTypes
}

/**
 * This interface defines, what filter criteria are accepted by the API for Discussion
 */
export interface IDiscussionFilterCriteria extends IFilterCriteria {
  /** key or keys defined in ContentManagementHelper */
  contentId?: AvailableContentItems | AvailableContentItems[]
  /** ID of one or many IUser */
  createdBy?: string | string[]
  /** ID of one or many IFeedbackInvitation */
  feedbackInvitation?: string | string[]
  /** order for creation date */
  "order[createdAt]"?: OrderTypes
  /** order for date of the last post */
  "order[lastPostDate]"?: OrderTypes
  /** IRI of a related object */
  relatedObject?: string
}

/**
 * This interface defines, what filter criteria are accepted by the API for FeedbackPosts
 */
export interface IFeedbackPostFilterCriteria extends IFilterCriteria {
  /** ID of one or more discussions */
  discussion?: string | string[]
  /** order for creation date */
  "order[createdAt]"?: OrderTypes
}



/* ************************************************************************** */
/* Selectors for FeedbackInvitations                                          */
/* ************************************************************************** */


/**
 * Select a FeedbackInvitation that is specified by its ID.
 */
export const selectFeedbackInvitationByIdentifier = (state: AppState, identifier: string): IFeedbackInvitation =>
  selectCollection<IFeedbackInvitation>(state, EntityType.FeedbackInvitation)
    .filter((f) => f.id.toString() === identifier)
    .shift()


/* ************************************************************************** */
/* Selectors for Discussions                                                  */
/* ************************************************************************** */

/**
 * filters the store for Discussions by given user and fbi, returns an array of Discussions
 *
 * user may be null for logged out users, therefor the selector filters for user === null too!
 */
export const selectDiscussionsByUserAndFBI = (state: AppState, user: IUser, fbi: IFeedbackInvitation): IDiscussion[] =>
  selectCollection<IDiscussion>(state, EntityType.Discussion)
    ?.filter(disc => fbi && idFromIModelOrIRI(disc.createdBy) === user?.id && idFromIModelOrIRI(disc.feedbackInvitation as IFeedbackInvitation) === fbi.id)
    .sort((a, b) => a.createdAt > b.createdAt ? -1 : 1)

/**
 * filters the store for a Discussion by given id
 */
export const selectDiscussionById = (state: AppState, id: number): IDiscussion =>
  selectCollection<IDiscussion>(state, EntityType.Discussion)
    ?.filter(disc => disc.id === id).shift()
